if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();

      this.zoomLevel = 1;
      this.minZoom = 1;
      this.maxZoom = 2.5;
      this.desktopZoomSequence = [1.5, 2, 2.5, 1];
      this.desktopZoomStepIndex = 0;
      this.desktopPointerMoved = false;
      this.panX = 0;
      this.panY = 0;
      this.activeMedia = null;
      this.activeMediaIndex = 0;
      this.pointerState = null;
      this.pinchState = null;
      this.swipeState = null;
      this.modalTapState = { time: 0, x: 0, y: 0 };
      this.desktopZoomFeedbackTimer = null;

      this.bindZoomInteractions();
      this.bindTouchInteractions();
      this.bindCloseInteractions();
    }

    get content() {
      return this.querySelector('.product-media-modal__content');
    }

    get allModalItems() {
      return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]')).filter((item) => {
        if (item.hasAttribute('hidden')) return false;
        if (item.matches('[data-gang-option]:not(.gang__active)')) return false;
        return true;
      });
    }

    get mediaItems() {
      return this.allModalItems.filter((item) => this.isMediaItemImage(item));
    }

    bindCloseInteractions() {
      this.addEventListener('click', (event) => {
        if (!this.hasAttribute('open')) return;

        const close = event.target.closest('[data-product-modal-close]');
        if (close) {
          event.preventDefault();
          this.hide();
          return;
        }

        if (event.target === this || event.target === this.querySelector('.product-media-modal__dialog')) {
          this.hide();
        }
      });
    }

    bindZoomInteractions() {
      this.addEventListener('click', (event) => {
        const zoomIn = event.target.closest('[data-product-modal-zoom-in]');
        const zoomOut = event.target.closest('[data-product-modal-zoom-out]');
        const zoomReset = event.target.closest('[data-product-modal-zoom-reset]');
        const prev = event.target.closest('[data-product-modal-prev]');
        const next = event.target.closest('[data-product-modal-next]');
        const thumb = event.target.closest('[data-product-modal-thumb]');

        if (zoomIn) {
          event.preventDefault();
          this.setZoom(this.zoomLevel + 0.5);
          return;
        }

        if (zoomOut) {
          event.preventDefault();
          this.setZoom(this.zoomLevel - 0.5);
          return;
        }

        if (zoomReset) {
          event.preventDefault();
          this.resetZoom();
          return;
        }

        if (prev) {
          event.preventDefault();
          this.showMediaByOffset(-1);
          return;
        }

        if (next) {
          event.preventDefault();
          this.showMediaByOffset(1);
          return;
        }

        if (thumb) {
          event.preventDefault();
          this.showMediaById(thumb.getAttribute('data-media-id'));
          return;
        }

        if (this.shouldHandleDesktopImageClick(event)) {
          event.preventDefault();

          if (this.desktopPointerMoved) {
            this.desktopPointerMoved = false;
            return;
          }

          this.cycleDesktopZoom(event);
        }
      });

      this.addEventListener('dblclick', (event) => {
        if (!this.hasAttribute('open') || !this.isActiveMediaImage() || this.isDesktopViewport()) return;
        if (!event.target.closest('.product-media-modal__content img')) return;
        event.preventDefault();
        this.toggleZoom();
      });

      this.addEventListener('pointerdown', this.handlePointerDown.bind(this));
      this.addEventListener('pointermove', this.handlePointerMove.bind(this));
      this.addEventListener('pointerup', this.handlePointerUp.bind(this));
      this.addEventListener('pointercancel', this.handlePointerUp.bind(this));

      this.addEventListener('wheel', (event) => {
        if (!this.hasAttribute('open') || !this.isActiveMediaImage()) return;
        if (!event.ctrlKey && Math.abs(event.deltaY) < 24) return;
        event.preventDefault();
        this.setZoom(this.zoomLevel + (event.deltaY < 0 ? 0.35 : -0.35));
      }, { passive: false });
    }

    bindTouchInteractions() {
      this.addEventListener('touchstart', (event) => {
        if (!this.hasAttribute('open') || !this.isActiveMediaImage()) return;
        if (!event.target.closest('.product-media-modal__content')) return;

        if (event.touches.length === 2) {
          event.preventDefault();
          this.pointerState = null;
          this.classList.remove('is-panning');
          const first = event.touches[0];
          const second = event.touches[1];

          this.pinchState = {
            distance: this.getTouchDistance(first, second),
            center: this.getTouchCenter(first, second),
            zoom: this.zoomLevel,
            panX: this.panX,
            panY: this.panY
          };
          return;
        }

        if (event.touches.length === 1 && !this.pinchState) {
          const touch = event.touches[0];
          this.swipeState = {
            x: touch.clientX,
            y: touch.clientY,
            time: Date.now()
          };
        }
      }, { passive: false });

      this.addEventListener('touchmove', (event) => {
        if (!this.hasAttribute('open') || !this.isActiveMediaImage()) return;

        if (event.touches.length === 2 && this.pinchState) {
          event.preventDefault();

          const first = event.touches[0];
          const second = event.touches[1];
          const distance = this.getTouchDistance(first, second);
          const center = this.getTouchCenter(first, second);
          const scale = distance / Math.max(1, this.pinchState.distance);
          const nextZoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.pinchState.zoom * scale));

          this.zoomLevel = Number(nextZoom.toFixed(2));

          if (this.zoomLevel <= 1.01) {
            this.panX = 0;
            this.panY = 0;
          } else {
            this.panX = this.pinchState.panX + center.x - this.pinchState.center.x;
            this.panY = this.pinchState.panY + center.y - this.pinchState.center.y;
            this.clampPan();
          }

          this.applyZoom();
          this.updateControls();
          return;
        }

        if (event.touches.length === 1 && this.zoomLevel > 1) {
          event.preventDefault();
        }
      }, { passive: false });

      this.addEventListener('touchend', (event) => {
        if (!this.hasAttribute('open')) return;

        if (this.pinchState) {
          event.preventDefault();

          if (event.touches.length < 2) {
            if (this.zoomLevel < 1.08) {
              this.resetZoom();
            } else {
              this.setZoom(this.zoomLevel);
            }

            this.pinchState = null;
            this.swipeState = null;
          }

          return;
        }

        if (!this.swipeState || event.changedTouches.length !== 1 || this.zoomLevel > 1) {
          this.swipeState = null;
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.swipeState.x;
        const deltaY = touch.clientY - this.swipeState.y;
        const elapsed = Date.now() - this.swipeState.time;
        this.swipeState = null;

        if (elapsed < 500 && Math.abs(deltaX) > 70 && Math.abs(deltaY) < 55) {
          event.preventDefault();
          this.showMediaByOffset(deltaX < 0 ? 1 : -1);
        }
      }, { passive: false });

      this.addEventListener('touchcancel', () => {
        this.pinchState = null;
        this.swipeState = null;
      }, { passive: true });
    }

    hide() {
      this.resetZoom();
      this.pointerState = null;
      this.pinchState = null;
      this.swipeState = null;
      this.clearDesktopZoomFeedback();
      super.hide();
      window.pauseAllMedia();
    }

    show(opener) {
      super.show(opener);
      this.showActiveMedia();
    }

    showActiveMedia() {
      const mediaId = this.openedBy ? this.openedBy.getAttribute('data-media-id') : null;
      const mediaItems = this.mediaItems;
      let activeMedia = mediaId ? mediaItems.find((item) => item.getAttribute('data-media-id') === mediaId) : null;

      if (!activeMedia) activeMedia = mediaItems[0];
      if (!activeMedia) return;

      this.setActiveMedia(activeMedia);
    }

    setActiveMedia(media) {
      this.allModalItems.forEach((item) => {
        item.classList.remove('is-active');
        if (!this.isMediaItemImage(item)) item.setAttribute('hidden', '');
      });

      media.removeAttribute('hidden');
      media.classList.add('is-active');

      this.activeMedia = media;
      this.activeMediaIndex = this.mediaItems.indexOf(media);
      this.resetZoom();
      this.updateControls();
    }

    showMediaByOffset(offset) {
      const mediaItems = this.mediaItems;
      if (!mediaItems.length) return;

      const currentIndex = Math.max(0, this.activeMediaIndex);
      const nextIndex = (currentIndex + offset + mediaItems.length) % mediaItems.length;
      this.setActiveMedia(mediaItems[nextIndex]);
    }

    showMediaById(mediaId) {
      if (!mediaId) return;

      const media = this.mediaItems.find((item) => item.getAttribute('data-media-id') === mediaId);
      if (media) this.setActiveMedia(media);
    }

    isMediaItemImage(item) {
      return !!(item && item.classList.contains('media-wrapper') && item.querySelector('.media > img'));
    }

    getActiveImage() {
      if (!this.activeMedia || !this.isActiveMediaImage()) return null;
      return this.activeMedia.querySelector('.media > img, img');
    }

    isActiveMediaImage() {
      return this.isMediaItemImage(this.activeMedia);
    }

    isDesktopViewport() {
      return window.matchMedia('(min-width: 750px)').matches;
    }

    shouldHandleDesktopImageClick(event) {
      if (!this.hasAttribute('open') || !this.isActiveMediaImage() || !this.isDesktopViewport()) return false;
      if (event.target.closest('button, a, .product-media-modal__toolbar, .product-media-modal__thumbs')) return false;
      return !!event.target.closest('.product-media-modal__content img');
    }

    cycleDesktopZoom(event) {
      if (!this.isActiveMediaImage()) return;

      const nextZoom = this.getNextDesktopZoomLevel();
      const zoomPoint = event ? { clientX: event.clientX, clientY: event.clientY } : null;
      this.setZoom(nextZoom, zoomPoint);
      this.showDesktopZoomFeedback();
    }

    getNextDesktopZoomLevel() {
      const currentZoom = Number(this.zoomLevel || 1);

      if (currentZoom < 1.25) return 1.5;
      if (currentZoom < 1.75) return 2;
      if (currentZoom < 2.25) return 2.5;
      return 1;
    }

    showDesktopZoomFeedback() {
      if (!this.isDesktopViewport() || !this.isActiveMediaImage()) return;

      this.clearDesktopZoomFeedback();
      this.classList.add('is-zoom-feedback');

      this.desktopZoomFeedbackTimer = window.setTimeout(() => {
        this.classList.remove('is-zoom-feedback');
        this.desktopZoomFeedbackTimer = null;
      }, 420);
    }

    clearDesktopZoomFeedback() {
      if (this.desktopZoomFeedbackTimer) {
        window.clearTimeout(this.desktopZoomFeedbackTimer);
        this.desktopZoomFeedbackTimer = null;
      }

      this.classList.remove('is-zoom-feedback');
    }

    toggleZoom() {
      if (!this.isActiveMediaImage()) return;
      this.setZoom(this.zoomLevel > 1 ? 1 : 2.4);
    }

    setZoom(value, zoomPoint = null) {
      if (!this.isActiveMediaImage()) return;

      const previousZoom = this.zoomLevel;
      const nextZoom = Math.min(this.maxZoom, Math.max(this.minZoom, Number(value) || this.minZoom));
      this.zoomLevel = Number(nextZoom.toFixed(2));

      if (this.zoomLevel <= 1.01) {
        this.zoomLevel = 1;
        this.panX = 0;
        this.panY = 0;
      } else if (zoomPoint && Number.isFinite(zoomPoint.clientX) && Number.isFinite(zoomPoint.clientY)) {
        this.setPanFromZoomPoint(previousZoom, this.zoomLevel, zoomPoint.clientX, zoomPoint.clientY);
      } else {
        this.clampPan();
      }

      this.applyZoom();
      this.updateControls();
    }

    setPanFromZoomPoint(previousZoom, nextZoom, clientX, clientY) {
      const image = this.getActiveImage();
      if (!image || nextZoom <= 1) {
        this.panX = 0;
        this.panY = 0;
        return;
      }

      const rect = image.getBoundingClientRect();
      const currentZoom = Math.max(this.minZoom, Number(previousZoom) || this.minZoom);
      const transformedCenterX = rect.left + rect.width / 2;
      const transformedCenterY = rect.top + rect.height / 2;
      const originalCenterX = transformedCenterX - this.panX;
      const originalCenterY = transformedCenterY - this.panY;
      const localX = (clientX - originalCenterX - this.panX) / currentZoom;
      const localY = (clientY - originalCenterY - this.panY) / currentZoom;

      this.panX = clientX - originalCenterX - localX * nextZoom;
      this.panY = clientY - originalCenterY - localY * nextZoom;
      this.clampPan();
    }

    resetZoom() {
      this.clearDesktopZoomFeedback();
      this.desktopZoomStepIndex = 0;
      this.desktopPointerMoved = false;
      this.zoomLevel = 1;
      this.panX = 0;
      this.panY = 0;
      this.applyZoom();
      this.updateControls();
    }

    applyZoom() {
      const image = this.getActiveImage();
      if (!image) {
        this.classList.remove('is-zoomed');
        return;
      }

      image.style.setProperty('--caleo-product-zoom-scale', this.zoomLevel);
      image.style.setProperty('--caleo-product-zoom-x', `${this.panX}px`);
      image.style.setProperty('--caleo-product-zoom-y', `${this.panY}px`);
      this.classList.toggle('is-zoomed', this.zoomLevel > 1);
    }

    clampPan() {
      const image = this.getActiveImage();
      const content = this.content;
      if (!image || !content || this.zoomLevel <= 1) {
        this.panX = 0;
        this.panY = 0;
        return;
      }

      const contentRect = content.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      const imageWidth = image.offsetWidth || imageRect.width / this.zoomLevel;
      const imageHeight = image.offsetHeight || imageRect.height / this.zoomLevel;
      const maxX = Math.max(0, ((imageWidth * this.zoomLevel) - contentRect.width) / 2 + 32);
      const maxY = Math.max(0, ((imageHeight * this.zoomLevel) - contentRect.height) / 2 + 32);

      this.panX = Math.min(maxX, Math.max(-maxX, this.panX));
      this.panY = Math.min(maxY, Math.max(-maxY, this.panY));
    }

    updateControls() {
      const isImage = this.isActiveMediaImage();
      const mediaItems = this.mediaItems;
      const zoomIn = this.querySelector('[data-product-modal-zoom-in]');
      const zoomOut = this.querySelector('[data-product-modal-zoom-out]');
      const zoomReset = this.querySelector('[data-product-modal-zoom-reset]');
      const prev = this.querySelector('[data-product-modal-prev]');
      const next = this.querySelector('[data-product-modal-next]');
      const help = this.querySelector('.product-media-modal__toolbar-title-help');
      const thumbsWrapper = this.querySelector('[data-product-modal-thumbs]');
      const thumbs = Array.from(this.querySelectorAll('[data-product-modal-thumb]'));
      const activeMediaId = this.activeMedia?.getAttribute('data-media-id');
      const availableMediaIds = new Set(mediaItems.map((item) => item.getAttribute('data-media-id')));

      this.classList.toggle('product-media-modal--image-active', isImage);
      this.classList.toggle('product-media-modal--media-active', !isImage);

      [zoomIn, zoomOut, zoomReset].forEach((button) => {
        if (!button) return;
        button.disabled = !isImage;
      });

      if (zoomIn) zoomIn.disabled = !isImage || this.zoomLevel >= this.maxZoom;
      if (zoomOut) zoomOut.disabled = !isImage || this.zoomLevel <= this.minZoom;
      if (zoomReset) zoomReset.textContent = `${Math.round(this.zoomLevel * 100)}%`;

      [prev, next].forEach((button) => {
        if (!button) return;
        button.disabled = mediaItems.length <= 1;
      });

      if (thumbsWrapper) thumbsWrapper.hidden = mediaItems.length <= 1;

      thumbs.forEach((thumb) => {
        const mediaId = thumb.getAttribute('data-media-id');
        const isAvailable = availableMediaIds.has(mediaId);

        thumb.hidden = !isAvailable;
        thumb.classList.toggle('is-active', mediaId === activeMediaId);
        thumb.setAttribute('aria-current', mediaId === activeMediaId ? 'true' : 'false');

        if (mediaId === activeMediaId && isAvailable) {
          requestAnimationFrame(() => {
            thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          });
        }
      });

      if (help) {
        help.textContent = isImage
          ? (this.isDesktopViewport() ? 'Clique para ampliar ou reduzir. Arraste para mover.' : 'Use pinça ou toque duas vezes para ampliar')
          : 'Use as imagens para navegar';
      }
    }

    handlePointerDown(event) {
      if (!this.hasAttribute('open') || !this.isActiveMediaImage()) return;
      if (event.target.closest('button, a')) return;
      if (!event.target.closest('.product-media-modal__content')) return;
      if (this.pinchState) return;

      const image = this.getActiveImage();
      if (!image) return;

      const now = Date.now();
      const deltaTap = now - this.modalTapState.time;
      const tapDistance = Math.hypot(event.clientX - this.modalTapState.x, event.clientY - this.modalTapState.y);

      if (!this.isDesktopViewport() && deltaTap > 0 && deltaTap < 320 && tapDistance < 28) {
        event.preventDefault();
        this.toggleZoom();
        this.modalTapState = { time: 0, x: 0, y: 0 };
        return;
      }

      this.modalTapState = { time: now, x: event.clientX, y: event.clientY };

      if (this.zoomLevel <= 1) return;

      event.preventDefault();
      this.pointerState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        panX: this.panX,
        panY: this.panY,
        moved: false
      };

      image.setPointerCapture?.(event.pointerId);
      this.classList.add('is-panning');
    }

    handlePointerMove(event) {
      if (!this.pointerState || this.pointerState.pointerId !== event.pointerId || this.pinchState) return;

      event.preventDefault();

      const deltaX = event.clientX - this.pointerState.startX;
      const deltaY = event.clientY - this.pointerState.startY;

      if (Math.hypot(deltaX, deltaY) > 6) {
        this.pointerState.moved = true;
        this.desktopPointerMoved = this.isDesktopViewport();
      }

      this.panX = this.pointerState.panX + deltaX;
      this.panY = this.pointerState.panY + deltaY;
      this.clampPan();
      this.applyZoom();
    }

    handlePointerUp(event) {
      if (!this.pointerState || this.pointerState.pointerId !== event.pointerId) return;

      const image = this.getActiveImage();
      const moved = this.pointerState.moved;
      image?.releasePointerCapture?.(event.pointerId);
      this.pointerState = null;
      this.classList.remove('is-panning');

      if (moved && this.isDesktopViewport()) {
        window.setTimeout(() => {
          this.desktopPointerMoved = false;
        }, 120);
      }
    }

    getTouchDistance(first, second) {
      return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
    }

    getTouchCenter(first, second) {
      return {
        x: (first.clientX + second.clientX) / 2,
        y: (first.clientY + second.clientY) / 2
      };
    }
  });
}

(function setupCaleoProductZoomOpeners() {
  if (window.CaleoProductZoomOpenersReady) return;
  window.CaleoProductZoomOpenersReady = true;

  const isMobileViewport = () => window.matchMedia('(max-width: 749px)').matches;

  document.addEventListener('click', (event) => {
    if (!isMobileViewport()) return;

    const mediaOpener = event.target.closest('modal-opener.product__modal-opener--video, modal-opener.product__modal-opener--external_video, modal-opener.product__modal-opener--model');
    if (!mediaOpener || mediaOpener.hasAttribute('data-caleo-product-zoom-opener')) return;

    const mediaId = mediaOpener.querySelector('[data-media-id]')?.getAttribute('data-media-id');
    const poster = mediaId ? document.getElementById(`Deferred-Poster-${mediaId}`) : null;
    const fallbackPoster = mediaOpener.nextElementSibling?.querySelector('.deferred-media__poster');
    const trigger = poster || fallbackPoster;

    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    trigger.click();
  }, true);

  document.addEventListener('click', (event) => {
    const zoomButton = event.target.closest('[data-caleo-product-zoom-button]');
    if (!zoomButton) return;

    const opener = zoomButton.closest('modal-opener[data-caleo-product-zoom-opener]');
    if (!opener) return;

    const modalSelector = opener.getAttribute('data-modal');
    const modal = modalSelector ? document.querySelector(modalSelector) : null;
    if (!modal || typeof modal.show !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    modal.show(zoomButton);
  }, true);

  const pointerStarts = new Map();
  let lastTap = { time: 0, opener: null, x: 0, y: 0 };

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch') return;

    const opener = event.target.closest('[data-caleo-product-zoom-opener]');
    if (!opener || event.target.closest('[data-caleo-product-zoom-button]')) return;

    pointerStarts.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      time: Date.now(),
      opener
    });
  }, { passive: true });

  document.addEventListener('pointerup', (event) => {
    if (event.pointerType !== 'touch') return;

    const start = pointerStarts.get(event.pointerId);
    pointerStarts.delete(event.pointerId);
    if (!start) return;

    const opener = start.opener;
    if (!opener || event.target.closest('button, a')) return;

    const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (distance > 12) return;

    const now = Date.now();
    const timeSinceLastTap = now - lastTap.time;
    const sameOpener = lastTap.opener === opener;
    const tapDistance = Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y);

    if (sameOpener && timeSinceLastTap < 340 && tapDistance < 30) {
      event.preventDefault();
      opener.querySelector('[data-caleo-product-zoom-button]')?.click();
      lastTap = { time: 0, opener: null, x: 0, y: 0 };
      return;
    }

    lastTap = { time: now, opener, x: event.clientX, y: event.clientY };
  });

  document.addEventListener('dblclick', (event) => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const opener = event.target.closest('[data-caleo-product-zoom-opener]');
    if (!opener || event.target.closest('[data-caleo-product-zoom-button]')) return;

    event.preventDefault();
    opener.querySelector('[data-caleo-product-zoom-button]')?.click();
  });
})();
